import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { ProjectId, ServiceId, UnitOfMeasureId, UnitTypeId, UserId } from '../shared/ids';
import { DEFAULT_UNITS } from '../schedule/unit-of-measure';
import { contourIsArea, serviceElement, type WithElement, type WithSideMargin } from './applies-to';
import { UNIT_BY_MEASURE } from './default-services';
import { totalFor, type GeometryTotals } from './geometry';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';

/**
 * A quantidade do serviço, por tipologia (Fase 24, fatia 5 — D1 a D5).
 *
 * O quadro Serviços × traçado mostrava o que o desenho determina e a planilha orçou, e
 * parava aí: o serviço que o desenho não mede ficava com um travessão, e o que o desenho
 * mede errado não tinha onde ser corrigido. Aqui o serviço ganha **número próprio em cada
 * tipologia**, e é ele que o índice de consumo multiplica (fatia 6).
 *
 * ## Dois números, e por que dois
 *
 * O **medido** é o que o "Mede por" responde — 12,4 m³ de concreto de pilar. É a base do
 * índice: "3 HH por m³" só faz sentido sobre metro cúbico.
 *
 * A **quantidade na unidade do serviço** é a da meta e da medição do planejamento
 * (`services.unit_of_measure_id`, RN-232). Quase sempre é o mesmo número. Quando a unidade
 * do cadastro não é a da medida — o pilar medido em "un" —, os dois divergem, e nenhum dos
 * dois está errado: o planejamento conta 24 pilares, o almoxarife compra 12,4 m³.
 *
 * Este módulo é a regra; a tela e o caso de uso a chamam. Sem banco, para que o teste monte
 * a situação inteira em memória.
 */

// ── Como o número nasce ─────────────────────────────────────────────────────────

/**
 * De onde o serviço tira o número nesta tipologia.
 *
 * - `DESENHO`: a peça é uma que o desenho traça, e o número é o traçado — corrigível, com
 *   motivo (D4);
 * - `DIGITADO`: a peça é "nenhum" (unidade, modelo) — o desenho não tem o que dizer, e o
 *   número digitado **é** a quantidade, não uma discordância (D1);
 * - `PENDENTE`: volume ou espala sem peça escolhida. Não mede nada e não aceita número:
 *   dar número a ele seria decidir, calado, se é o pilar ou a viga (D17).
 */
export type ServiceMeasuring = 'DESENHO' | 'DIGITADO' | 'PENDENTE';

export function serviceMeasuring(service: WithElement): ServiceMeasuring {
  const elemento = serviceElement(service);
  if (elemento === undefined) return 'PENDENTE';
  return elemento === 'NENHUM' ? 'DIGITADO' : 'DESENHO';
}

/**
 * O que a tipologia decidiu sobre um serviço — só o que alguém digitou.
 *
 * **Ausente é o caso comum**: o serviço que o desenho mede segue o traçado, e o que ele não
 * mede ainda não tem número. As duas coisas se leem da ausência, como a quantidade do item
 * (RN-253) — guardar uma cópia do traçado transformaria "ninguém corrigiu" em "alguém
 * confirmou este número", e o desenho redesenhado amanhã não mudaria mais nada.
 */
export interface ServiceQuantityDecision {
  /** O medido digitado: a correção (serviço do desenho) ou a quantidade (serviço digitado). */
  readonly measured?: number;
  /** Por que o medido discorda do traçado. Só existe na correção (D4). */
  readonly reason?: string;
  /** A quantidade na unidade do serviço, digitada quando as unidades divergem (D5). */
  readonly quantity?: number;
}

/** De onde veio o medido — a tela escreve "corrigido" e o de → para a partir daqui. */
export type MeasuredOrigin = 'TRACADO' | 'CORRIGIDO' | 'DIGITADO';

/** De onde veio o número na unidade do serviço. */
export type InServiceUnitOrigin = 'MEDIDO' | 'CONTADO' | 'DIGITADO';

export interface ResolvedServiceQuantity {
  readonly measuring: ServiceMeasuring;
  /** O medido — a base do índice. `undefined` é "ainda não tem número", e não zero. */
  readonly measured: number | undefined;
  readonly measuredOrigin: MeasuredOrigin | undefined;
  /**
   * O que o desenho diz **hoje**, ao lado da correção.
   *
   * Ao vivo, e não o de quando alguém corrigiu: se o desenho mudar depois, a correção
   * continua mandando e o traçado novo aparece ao lado (D1) — é o que avisa que a correção
   * talvez tenha envelhecido.
   */
  readonly traced: number | undefined;
  readonly reason?: string;
  /** O código da unidade em que o desenho responde. Ausente no digitado: lá é a do serviço. */
  readonly measureUnitCode?: string;
  /** A unidade do cadastro não é a da medida — e aí há dois números (D5). */
  readonly unitsDiverge: boolean;
  /** A quantidade na unidade do serviço — a da meta e da medição. */
  readonly inServiceUnit: number | undefined;
  readonly inServiceUnitOrigin: InServiceUnitOrigin | undefined;
}

/**
 * A unidade em que o desenho responde este serviço — `m2`, `m`, `m3`.
 *
 * Só no serviço do desenho: o digitado não tem medida própria, e o número dele já nasce na
 * unidade do serviço. A tabela é a de `UNIT_BY_MEASURE`, a mesma que semeou os serviços do
 * catálogo — duas tabelas de "que unidade tem a área de parede" divergiriam na primeira
 * medida nova.
 *
 * A exceção é o contorno do radier e da laje: a borda é perímetro × espessura, em m² (Fase 27,
 * etapa 3), e não o metro do requadro de vão. Sem ela, a fôrma de borda do radier, em m², diria
 * que a unidade dela diverge da medida.
 */
export function measureUnitCode(service: WithElement): string | undefined {
  if (serviceMeasuring(service) !== 'DESENHO') return undefined;
  if (service.measureBy === 'CONTOUR' && contourIsArea(serviceElement(service))) return 'm2';
  return UNIT_BY_MEASURE[service.measureBy];
}

/**
 * `m2`, `M²` e `m²` são a mesma unidade.
 *
 * O cadastro escreve o código (`m2`), a planilha escreve o expoente (`M²`), e a caixa não
 * diz nada. Comparar o texto cru faria o pilar em "M3" divergir do próprio metro cúbico.
 */
export function sameUnit(a: string, b: string): boolean {
  const limpa = (texto: string) =>
    normalizeName(texto).replace(/²/g, '2').replace(/³/g, '3').replace(/\s/g, '');
  return limpa(a) === limpa(b);
}

/** `m2` → `m²`: o nome do catálogo de unidades, para a tela. O código fica se não houver. */
export function unitLabel(code: string): string {
  return DEFAULT_UNITS.find((unidade) => sameUnit(unidade.code, code))?.name ?? code;
}

/**
 * Quantas peças o desenho tem para este serviço — só pilar e viga contam.
 *
 * `undefined` é "não há o que contar": serviço de outra peça, ou total sem contagem.
 */
function pecasContadas(service: WithElement, totals: GeometryTotals): number | undefined {
  const elemento = serviceElement(service);
  if (elemento !== 'PILAR' && elemento !== 'VIGA') return undefined;
  return totals.countByKind?.[elemento];
}

export interface ResolveServiceQuantityInput {
  /** Com a margem: a largura da soleira e do peitoril sai com ela (RN-308). */
  readonly service: WithSideMargin;
  /** O que o desenho determina para a tipologia — ou para o prédio, já multiplicado. */
  readonly totals: GeometryTotals;
  readonly decision?: ServiceQuantityDecision;
  /** O código da unidade do cadastro do serviço (RN-232). Ausente = sem unidade escolhida. */
  readonly serviceUnitCode?: string;
}

/**
 * Os dois números do serviço nesta tipologia (D1, D4, D5).
 *
 * O medido: a correção manda sobre o traçado; no serviço digitado, o digitado é o número; no
 * pendente, não há número. A unidade do serviço: igual ao medido quando as unidades
 * coincidem — ou quando o serviço ainda não tem unidade, que não é divergência de nada —;
 * contada na planta no pilar e na viga em "un"; digitada no resto.
 *
 * A contagem só vale para "un". O pilar em "kg" de aço diverge do metro cúbico e **não** é a
 * quantidade de peças: ali o número é digitado, como em qualquer outra divergência.
 */
export function resolveServiceQuantity(
  input: ResolveServiceQuantityInput,
): ResolvedServiceQuantity {
  const { service, totals, decision } = input;
  const measuring = serviceMeasuring(service);
  const traced = measuring === 'DESENHO' ? totalFor(service, totals) : undefined;

  const corrigido = measuring === 'DESENHO' ? decision?.measured : undefined;
  const digitado = measuring === 'DIGITADO' ? decision?.measured : undefined;
  const measured = corrigido ?? digitado ?? traced;
  const measuredOrigin: MeasuredOrigin | undefined =
    corrigido !== undefined
      ? 'CORRIGIDO'
      : digitado !== undefined
        ? 'DIGITADO'
        : traced !== undefined
          ? 'TRACADO'
          : undefined;

  const daMedida = measureUnitCode(service);
  const unitsDiverge =
    daMedida !== undefined &&
    input.serviceUnitCode !== undefined &&
    !sameUnit(daMedida, input.serviceUnitCode);

  const base = {
    measuring,
    measured,
    measuredOrigin,
    traced,
    unitsDiverge,
    ...(corrigido === undefined || decision?.reason === undefined
      ? {}
      : { reason: decision.reason }),
    ...(daMedida === undefined ? {} : { measureUnitCode: daMedida }),
  };

  if (!unitsDiverge) {
    return {
      ...base,
      inServiceUnit: measured,
      inServiceUnitOrigin: measured === undefined ? undefined : 'MEDIDO',
    };
  }

  const contadas =
    input.serviceUnitCode !== undefined && sameUnit(input.serviceUnitCode, 'un')
      ? pecasContadas(service, totals)
      : undefined;
  if (contadas !== undefined) {
    return { ...base, inServiceUnit: contadas, inServiceUnitOrigin: 'CONTADO' };
  }

  const naUnidade = decision?.quantity;
  return {
    ...base,
    inServiceUnit: naUnidade,
    inServiceUnitOrigin: naUnidade === undefined ? undefined : 'DIGITADO',
  };
}

/**
 * O número na unidade do serviço é digitado aqui? É o que a tela pergunta para abrir o campo.
 *
 * Só quando as unidades divergem e a planta não conta: nos outros casos ele é o medido, ou
 * as peças, e digitar por cima seria um terceiro número sem dono.
 */
export function typesInServiceUnit(resolved: ResolvedServiceQuantity): boolean {
  return resolved.unitsDiverge && resolved.inServiceUnitOrigin !== 'CONTADO';
}

// ── A decisão, conferida ────────────────────────────────────────────────────────

/** Seis casas: é o que a coluna guarda (`numeric(14, 6)`), como na quantidade do item. */
const CASAS = 6;

function arredondada(valor: number): number {
  const fator = 10 ** CASAS;
  return Math.round(valor * fator) / fator;
}

function negativa(servico: string, quantity: number): DomainError {
  return domainError(
    TAKEOFF_ERROR.NEGATIVE_SERVICE_QUANTITY,
    `${TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.NEGATIVE_SERVICE_QUANTITY]!} Serviço: ${servico}.`,
    { service: servico, quantity },
  );
}

/**
 * A correção deste serviço dispensa o motivo? Só a da **escada** (Fase 27, D10, RN-373).
 *
 * A conta da escada é aproximada — o degrau ingrauxido, o lance em leque —, e corrigi-la é o
 * normal, não uma discordância que alguém precise justificar. Em todo outro elemento a correção
 * continua pedindo o motivo.
 */
export function correctionNeedsNoReason(service: WithElement): boolean {
  return serviceElement(service) === 'ESCADA';
}

/**
 * O medido digitado, conferido (D1, D4).
 *
 * - No serviço do **desenho**, é correção, e correção sem motivo é recusada: seis meses
 *   depois, ninguém sabe por que o reboco tem 30 m² a menos que a planta. A exceção é o serviço
 *   da escada, que se corrige sem motivo (RN-373) — o motivo, se vier, é guardado.
 * - No serviço **digitado**, é a quantidade, e o motivo não é pedido — nem guardado: ali não
 *   há de que discordar.
 * - No **pendente**, não há número a aceitar: primeiro a peça.
 */
export function decideMeasured(
  service: WithElement & { readonly name: string },
  measured: number,
  reason: string | undefined,
): Result<{ readonly measured: number; readonly reason?: string }, DomainError> {
  const measuring = serviceMeasuring(service);
  if (measuring === 'PENDENTE') {
    return err(
      domainError(
        TAKEOFF_ERROR.SERVICE_NOT_MEASURABLE,
        `${TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.SERVICE_NOT_MEASURABLE]!} Serviço: ${service.name}.`,
        { service: service.name },
      ),
    );
  }
  if (!Number.isFinite(measured) || measured < 0) return err(negativa(service.name, measured));

  if (measuring === 'DIGITADO') return ok({ measured: arredondada(measured) });

  const motivo = reason?.trim() ?? '';
  if (motivo === '' && correctionNeedsNoReason(service)) {
    return ok({ measured: arredondada(measured) });
  }
  if (motivo === '') {
    return err(
      domainError(
        TAKEOFF_ERROR.MEASURED_REASON_REQUIRED,
        `${TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.MEASURED_REASON_REQUIRED]!} Serviço: ${service.name}.`,
        { service: service.name },
      ),
    );
  }
  return ok({ measured: arredondada(measured), reason: motivo });
}

/** A quantidade na unidade do serviço, conferida. Zero é aceito; negativo, não. */
export function decideInServiceUnit(
  service: { readonly name: string },
  quantity: number,
): Result<number, DomainError> {
  if (!Number.isFinite(quantity) || quantity < 0) return err(negativa(service.name, quantity));
  return ok(arredondada(quantity));
}

// ── No banco ────────────────────────────────────────────────────────────────────

/**
 * A decisão da tipologia sobre um serviço, gravada (`0040`).
 *
 * Uma por tipologia × serviço, como a do item é uma por tipologia × item. Ausente é o caso
 * comum — segue o traçado, ou ainda não tem número —, e a linha só existe com ao menos um
 * dos dois números.
 */
export interface UnitTypeServiceQuantity {
  readonly unitTypeId: UnitTypeId;
  readonly serviceId: ServiceId;
  readonly projectId: ProjectId;
  readonly measured?: number;
  readonly measuredReason?: string;
  readonly quantity?: number;
  readonly updatedAt: string;
  readonly updatedByUserId?: UserId;
}

function chave(decisao: Pick<UnitTypeServiceQuantity, 'unitTypeId' | 'serviceId'>): string {
  return `${decisao.unitTypeId}|${decisao.serviceId}`;
}

function mesmaDecisao(a: UnitTypeServiceQuantity, b: UnitTypeServiceQuantity): boolean {
  return (
    a.measured === b.measured && a.measuredReason === b.measuredReason && a.quantity === b.quantity
  );
}

/**
 * O que mudou nas decisões de serviço, **dentro do escopo** — a regra de `diffItemQuantities`.
 *
 * O escopo são as tipologias que o envelope trouxe: a de fora fica como estava, e dentro
 * dele o que não veio sai. É assim que "voltar ao traçado" chega ao banco.
 */
export function diffServiceQuantities(input: {
  readonly scope: ReadonlySet<UnitTypeId>;
  readonly before: readonly UnitTypeServiceQuantity[];
  readonly after: readonly UnitTypeServiceQuantity[];
}): {
  readonly set: readonly UnitTypeServiceQuantity[];
  readonly cleared: readonly UnitTypeServiceQuantity[];
} {
  const antes = new Map(
    input.before
      .filter((decisao) => input.scope.has(decisao.unitTypeId))
      .map((decisao) => [chave(decisao), decisao]),
  );
  const depois = new Map(input.after.map((decisao) => [chave(decisao), decisao]));

  return {
    set: [...depois.entries()]
      .filter(([id, decisao]) => {
        const anterior = antes.get(id);
        return anterior === undefined || !mesmaDecisao(anterior, decisao);
      })
      .map(([, decisao]) => decisao),
    cleared: [...antes.entries()].filter(([id]) => !depois.has(id)).map(([, decisao]) => decisao),
  };
}

// ── A unidade do serviço ────────────────────────────────────────────────────────

/**
 * Trocar a unidade deste serviço é recusado? (D3)
 *
 * A unidade é **uma só por serviço**, a do cadastro, e é nela que a meta e a medição do
 * planejamento foram gravadas. Com medição gravada, trocar "m²" por "un" não converte nada:
 * os 340 lançados continuariam lá, lidos como 340 unidades. Por isso a troca é recusada, e
 * não "vale para frente" como o resto do cadastro (RN-237).
 *
 * Escolher a primeira unidade não troca nada, e é sempre aceito — o serviço sem unidade não
 * teve medição, porque medir sem unidade é recusado desde a Fase 21.
 */
export function unitChangeLocked(input: {
  readonly current?: UnitOfMeasureId;
  readonly next?: UnitOfMeasureId | undefined;
  readonly executedMeasurements: number;
}): boolean {
  return (
    input.current !== undefined && input.current !== input.next && input.executedMeasurements > 0
  );
}

export function serviceUnitLockedError(input: {
  readonly serviceName: string;
  readonly unitName?: string;
  readonly executedMeasurements: number;
}): DomainError {
  const lancamentos =
    input.executedMeasurements === 1
      ? '1 lançamento de quantidade executada'
      : `${String(input.executedMeasurements)} lançamentos de quantidade executada`;
  return domainError(
    TAKEOFF_ERROR.SERVICE_UNIT_LOCKED,
    `${input.serviceName} já tem ${lancamentos}${
      input.unitName === undefined ? '' : ` em ${input.unitName}`
    }. Trocar a unidade mudaria o que essas medições querem dizer — elas não seriam convertidas. Para medir em outra unidade, cadastre outro serviço.`,
    {
      service: input.serviceName,
      executedMeasurements: input.executedMeasurements,
      ...(input.unitName === undefined ? {} : { unit: input.unitName }),
    },
  );
}
